// One card per tool call (design-revamp.md section 5, Tool cards): a plain label, its status, the
// arguments behind a disclosure, and a link for each AlphaLab page it returned, named by what it opens.
// Quiet beside the answer: no shadow, a light border, small muted text.
import type { ToolCallMessagePartProps } from "@assistant-ui/react";
import { CheckCircle2, ExternalLink, Loader2, Wrench, XCircle } from "lucide-react";
import { useContext } from "react";
import { linkLabel, toolLabel } from "../links.js";
import type { ToolCardData } from "../thread.js";
import { OpenLinkContext } from "./Thread.js";

export function ToolCard({ toolName, artifact }: ToolCallMessagePartProps) {
  const open = useContext(OpenLinkContext);
  const card = (artifact ?? { status: "completed", links: [] }) as ToolCardData;
  const icon =
    card.status === "running" ? (
      <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
    ) : card.status === "failed" ? (
      <XCircle className="size-3.5 text-error" />
    ) : (
      <CheckCircle2 className="size-3.5 text-muted-foreground/70" />
    );
  return (
    <div className="rounded-md border border-border/70 text-[13px] text-muted-foreground">
      <details className="group">
        <summary className="flex cursor-pointer list-none items-center gap-2 px-2.5 py-1.5 hover:text-foreground">
          {icon}
          <Wrench className="size-3 text-muted-foreground/70" />
          <span className="flex-1">{toolLabel(toolName)}</span>
          <span className="text-xs">{card.status === "running" ? "running" : card.status === "failed" ? "failed" : "done"}</span>
        </summary>
        <div className="space-y-2 border-t border-border/70 px-2.5 py-2">
          <p className="font-mono text-xs">{toolName}</p>
          {card.args && <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-muted p-2 font-mono text-xs text-foreground">{card.args}</pre>}
          {card.result && <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2 font-mono text-xs text-foreground">{card.result}</pre>}
        </div>
      </details>
      {card.links.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 border-t border-border/70 px-2.5 py-1.5">
          {card.links.map((url) => {
            const label = linkLabel(url, card.result);
            return (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={label}
                className="inline-flex min-w-0 max-w-full items-center gap-1 text-xs text-primary-ink hover:underline"
                onClick={(e) => {
                  if (open(url, label)) e.preventDefault();
                }}
              >
                <span className="truncate">{label}</span>
                <ExternalLink className="size-3 shrink-0" />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
